import { useEffect, useState } from "react";
import { CompanionWeapon, CompanionWeaponBlock, CompanionWeaponBlockBase } from "../../../model/character";
import { v4 } from 'uuid';

import { Box, Divider, FormGroup, Stack, styled, TextField, Typography } from "@mui/material";
import { NumberField } from "../../../components/formFields/NumberField";
import StatLookupField from "../../../components/StatLookupField";
import FormFieldLabel from "../../../components/formFields/FormFieldLabel";
import { Modal, ModalAction, ModalContentSection } from "../../../components/Modal";
import ButtonFlat, { ButtonFlatIcon } from "../../../components/ButtonFlat";

import RemoveIcon from '@mui/icons-material/Remove';
import AbilityEditor from "../../characterPage/AbilityEditor";
import { CreateDefaultCombatSkills } from "../../../master/data";
import TagList from "../../../components/TagList";
import LibraryModal from "../libraryModal";
import { StatDisplay, StrongHitRangeLabel } from "../../../components/StatDisplay";
import { defaultCompanionWeaponBase, defaultCompanionWeaponBlock } from "../../../master/defaults";

const WeaponBlockList = styled('div')({
    display: "flex",
    height: "33px"
})

const WeaponBlockListLabel = styled('div')({
    flexGrow: 1,
    paddingLeft: "8px",
    display: "flex",
})

const BlockListButton = styled('button')(({ theme }) => ({
    background: "unset",
    border: 0,
    outline: 0,
    cursor: "pointer",
    padding: 0,
    display: "inline",
    color: theme.palette.text.primary,
    fontSize: theme.typography.body1.fontSize,
    
    "&:hover": {
        textDecoration: "underline"
    }
}));

const CompanionWeaponBlockEditor = (props: {
        weaponBlock: CompanionWeaponBlock | CompanionWeaponBlockBase,
        setWeaponBlock: (block: CompanionWeaponBlock | CompanionWeaponBlockBase) => void,
        isBase?: boolean
    }
    ) => {
    const { weaponBlock: companionWeaponBlock, setWeaponBlock: setCompanionWeaponBlock } = props;

    const onChange = (key: string, value: any) => {
        setCompanionWeaponBlock({
            ...companionWeaponBlock,
            [key]: value
        })
    }

    const handleAddFeature = (item: string) => {
        const newFeatures = [ ...companionWeaponBlock.addFeatures, item ];
        onChange("addFeatures", newFeatures);

    }

    const handleRemoveFeature = (index: number) => {
        const features = [ ...companionWeaponBlock.addFeatures ];
        features.splice(index, 1);
        onChange("addFeatures", features);
    }

    const handleAddWeaponType = (item: string) => {
        const items = [ ...companionWeaponBlock.weaponType, item ];
        onChange("weaponType", items);

    }

    const handleRemoveWeaponType = (index: number) => {
        const items = [ ...companionWeaponBlock.weaponType ];
        items.splice(index, 1);
        onChange("weaponType", items);
    }

    return <>
        <Stack spacing={1}>
            {props.isBase !== true &&
            <FormFieldLabel label="Name">
                <TextField fullWidth type="text" variant="standard" value={companionWeaponBlock.name} onChange={(event) => onChange("name", event.currentTarget.value)} />
            </FormFieldLabel>}
            <FormFieldLabel label="Hit Dice" justifyContent="end">
                <NumberField step={1} min={0} value={companionWeaponBlock.hitDice} onChange={(value) => onChange("hitDice", value)} customRenderer={(val) => `${val}d6`}/>
            </FormFieldLabel>
            <FormFieldLabel label="Hit Bonus" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.hitBonus} onChange={(value) => onChange("hitBonus", value)} customRenderer={(v) => StatDisplay(v, "", true)} />
            </FormFieldLabel>
            {props.isBase &&
            <FormFieldLabel label="Skill" justifyContent="end"><StatLookupField onChange={(value) => onChange("skillBonusId", value)}
                //@ts-ignore
                value={companionWeaponBlock.skillBonusId}
                stats={[...CreateDefaultCombatSkills()]} />
                </FormFieldLabel> }
            <FormFieldLabel label="Range" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.range} onChange={(value) => onChange("range", value)} />
            </FormFieldLabel>
            <FormFieldLabel label="Endurance Damage" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.enduranceDamage} onChange={(value) => onChange("enduranceDamage", value)} />
            </FormFieldLabel>
            <FormFieldLabel label="Crit Damage" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.critDamage} onChange={(value) => onChange("critDamage", value)} />
            </FormFieldLabel>
            <FormFieldLabel label={`Strong Hit Range (${StrongHitRangeLabel(companionWeaponBlock.strongHitRange)})`} justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.strongHitRange} onChange={(value) => onChange("strongHitRange", value)}
                    customRenderer={(v) => !props.isBase ? StatDisplay(v) : v} />
            </FormFieldLabel>
            <FormFieldLabel label="Reload" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.reload} onChange={(value) => onChange("reload", value)}  customRenderer={(v) => !props.isBase ? StatDisplay(v) : v} />
            </FormFieldLabel>
            <FormFieldLabel label="Defence" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.defence} onChange={(value) => onChange("defence", value)}  customRenderer={(v) => !props.isBase ? StatDisplay(v) : v} />
            </FormFieldLabel>
            <FormFieldLabel label="Armour" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.armour} onChange={(value) => onChange("armour", value)}  customRenderer={(v) => !props.isBase ? StatDisplay(v) : v}/>
            </FormFieldLabel>
            <FormFieldLabel label="Durability" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.durability} onChange={(value) => onChange("durability", value)}  customRenderer={(v) => !props.isBase ? StatDisplay(v) : v}/>
            </FormFieldLabel>
            <FormFieldLabel label="Bodies" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.bodies} onChange={(value) => onChange("bodies", value)}  customRenderer={(v) => !props.isBase ? StatDisplay(v) : v}/>
            </FormFieldLabel>
            <FormFieldLabel label="Mobility" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.mobility} onChange={(value) => onChange("mobility", value)}  customRenderer={(v) => !props.isBase ? StatDisplay(v) : v}/>
            </FormFieldLabel>
            <FormFieldLabel label="Acquire" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.acquire} onChange={(value) => onChange("acquire", value)} customRenderer={(v) => StatDisplay(v, "t", !props.isBase)}/>
            </FormFieldLabel>
            <FormFieldLabel label="Influence" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.influence} onChange={(value) => onChange("influence", value)} customRenderer={(v) => !props.isBase ? StatDisplay(v) : v} />
            </FormFieldLabel>
            <FormFieldLabel label="Resources" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.resources} onChange={(value) => onChange("resources", value)} customRenderer={(v) => !props.isBase ? StatDisplay(v) : v} />
            </FormFieldLabel>
            <FormFieldLabel label="Knowledge" justifyContent="end">
                <NumberField step={1} value={companionWeaponBlock.knowledge} onChange={(value) => onChange("knowledge", value)} customRenderer={(v) => `${v}kn`} />
            </FormFieldLabel>
        </Stack>
        <ModalContentSection title="Weapon Type">
            <TagList items={companionWeaponBlock.weaponType} onAdd={handleAddWeaponType} onRemove={handleRemoveWeaponType} />
        </ModalContentSection>
        <ModalContentSection title="Keywords">
            <TagList items={companionWeaponBlock.addFeatures} onAdd={handleAddFeature} onRemove={handleRemoveFeature} />
        </ModalContentSection>
        <AbilityEditor title="Strong Hit Options" abilities={companionWeaponBlock.strongHits} setAbilities={(value) => onChange("strongHits", value)} />
        <AbilityEditor title="Passive Abilities" abilities={companionWeaponBlock.passives} setAbilities={(value) => onChange("passives", value)} />
        {props.isBase !== true && <Box paddingLeft={1}>
            <Typography>Notes</Typography>
            <TextField value={companionWeaponBlock.notes} onChange={(event) => onChange('notes', event.currentTarget.value)} multiline fullWidth />
        </Box>}
    </>
}

const defaultWeapon: CompanionWeapon = {
    base: { ...defaultCompanionWeaponBase },
    variations: [],
    modifications: [],
    id: "",
    isStashed: false,
    name: "New Companion (Weapon)",
    description: "",
    munitionsSpent: 0,
    reloadsPerformed: 0,
}

export const GetNewCompanion = (): CompanionWeapon => {
    return {
        ...defaultWeapon,
        id: v4()
    }
}

export const CompanionWeaponEditor = (props: {
        weapon?: CompanionWeapon,
        setWeapon: (value: CompanionWeapon) => void,
        onClose: () => void,
    }) => {
    const { weapon, setWeapon, onClose } = props;
    const [ current, setCurrent ] = useState<CompanionWeapon>(defaultWeapon);
    const [ currentVariation, setCurrentVariation ] = useState<number | undefined>(undefined);
    const [ currentModification, setCurrentModification ] = useState<number | undefined>(undefined);
    const [ selectVariation, setSelectVariation ] = useState(false);
    const [ selectModification, setSelectModification ] = useState(false);

    useEffect(() => {
            if(weapon !== undefined)
                setCurrent(weapon ?? defaultWeapon);
        }, [weapon]);

    const handleSelectVariation = (block: CompanionWeaponBlock) => {
        const variations = [ ...current.variations, { ...block, id: v4() } ]
        setCurrent((c) => ({
            ...c,
            variations
        }));
    }


    const handleAddVariation = () => {
        const variations = [ ...current.variations, { ...defaultCompanionWeaponBlock, name: "New Variation" } ]
        setCurrent({
            ...current,
            variations
        });
    }

    const handleRemoveVariation = (index: number) => {
        const variations = [ ...current.variations ];
        variations.splice(index,1);
        setCurrent({
            ...current,
            variations
        });
    }

    const handleChangeVariation = (index: number, variation: CompanionWeaponBlock) => {
        const variations = [ ...current.variations ];
        variations[index] = variation;
        setCurrent({
            ...current,
            variations
        });
    }

    const handleAddModification = () => {
        const modifications = [ ...current.modifications, { ...defaultCompanionWeaponBlock, name: "New Modification" } ]
        setCurrent({
            ...current,
            modifications
        });
    }
    
    const handleSelectModification = (block: CompanionWeaponBlock) => {
        const modifications = [ ...current.modifications, { ...block, id: v4() } ]
        setCurrent((c) => ({
            ...c,
            modifications
        }));
    }

    const handleRemoveModification = (index: number) => {
        const modifications = [ ...current.modifications ];
        modifications.splice(index,1);
        setCurrent({
            ...current,
            modifications
        });
    }

    const handleChangeModification = (index: number, modification: CompanionWeaponBlock) => {
        const modifications = [ ...current.modifications ];
        modifications[index] = modification;
        setCurrent({
            ...current,
            modifications
        });
    }
    
    const onChange = (key: string, value: any) => {
        setCurrent({
            ...current,
            [key]: value
        })
    }

    const handleModalSubmit = () => {
        setWeapon(current)
    }

    const actionList: ModalAction[] = [];
        if(currentModification !== undefined || currentVariation !== undefined) {
            actionList.push({
                label: "Return",
                onClick: () => { setCurrentModification(undefined); setCurrentVariation(undefined); },
                size: 12
            })
        }
        actionList.push({
            label: "Cancel",    
            onClick: onClose,
        });
        actionList.push({
            label: "Save",    
            onClick: handleModalSubmit,
            primary: true,
        });

    return <Modal
            open={weapon !== undefined}
            onClose={() => onClose()}
            actions={actionList}
            title={`Edit ${current.name}`}
            >
                <LibraryModal defaultItem={defaultCompanionWeaponBlock} onClose={() => setSelectVariation(false)} show={selectVariation} onSelect={handleSelectVariation} prefix="lcomwepv" title="Select Weapon Variation" />
                <LibraryModal defaultItem={defaultCompanionWeaponBlock} onClose={() => setSelectModification(false)} show={selectModification} onSelect={handleSelectModification} prefix="lcomwepm" title="Select Weapon Modification" />
                <FormGroup>
                <FormFieldLabel label="Name">
                    <TextField fullWidth type="text" variant="standard" value={current.name} onChange={(event) => onChange("name", event.currentTarget.value)} />
                </FormFieldLabel>
                
                <ModalContentSection title="Type and Variation" onAdd={handleAddVariation} collapsable>
                    {current.variations.map((variation, index) => {
                        return <WeaponBlockList>
                            <WeaponBlockListLabel><BlockListButton onClick={() => { setCurrentVariation(index); setCurrentModification(undefined); }}>{variation.name}</BlockListButton></WeaponBlockListLabel>
                            <ButtonFlatIcon onClick={() => handleRemoveVariation(index)}><RemoveIcon /></ButtonFlatIcon>
                        </WeaponBlockList>
                    })}
                </ModalContentSection>
                <ModalContentSection title="Modifications" onAdd={handleAddModification} collapsable>
                    {current.modifications.map((modification, index) => {
                        return <WeaponBlockList>
                            <WeaponBlockListLabel><BlockListButton onClick={() => { setCurrentModification(index); setCurrentVariation(undefined); }}>{modification.name}</BlockListButton></WeaponBlockListLabel>
                            <ButtonFlatIcon onClick={() => handleRemoveModification(index)}><RemoveIcon /></ButtonFlatIcon>
                        </WeaponBlockList>
                    })}
                </ModalContentSection>
                <Divider sx={{ marginY: 2}}/>

                {currentVariation !== undefined && <CompanionWeaponBlockEditor weaponBlock={current.variations[currentVariation]} setWeaponBlock={(wb) => handleChangeVariation(currentVariation, wb)} />}
                {currentModification !== undefined && <CompanionWeaponBlockEditor weaponBlock={current.modifications[currentModification]} setWeaponBlock={(wb) => handleChangeModification(currentModification, wb)} />}
                {currentModification === undefined && currentVariation === undefined && <CompanionWeaponBlockEditor weaponBlock={current.base} isBase setWeaponBlock={(wb) => onChange("base", wb)} />}
                <Box paddingLeft={1}>
                    <Typography>Description</Typography>
                    <TextField value={current.description} onChange={(event) => onChange('description', event.currentTarget.value)} multiline fullWidth />
                </Box>
            </FormGroup>
        </Modal>

    
}